import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import JSZip from 'jszip';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import {
  IconComponent,
  MessageService,
  ProgressComponent
} from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';
import { ApiService } from '@/core/api.service';
import { SessionService } from '@/core/session.service';
import { Credit } from '@/main/models/credit';
import { environment } from '@/environments/environment';
import { ProgressService } from '@/shared/progress.service';

@Component({
  selector: 'app-sharing-options',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconComponent,
    ProgressComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule
  ],
  templateUrl: './sharing-options.component.html',
  styleUrl: './sharing-options.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SharingOptionsComponent {
  // Dependency injection
  apiService = inject(ApiService);
  sessionService = inject(SessionService);
  data: { credit?: Credit; download: boolean } = inject(MAT_DIALOG_DATA);
  formBuilder = inject(FormBuilder);
  messageService = inject(MessageService);
  progressService = inject(ProgressService);

  // Events
  afterSubmit = output<Operation>();

  // Properties
  credit = signal<Credit | undefined>(undefined);
  form: FormGroup = new FormGroup({});
  sharing = signal<boolean>(false);
  dowloadedDocuments = signal<boolean>(false);
  documents: File[] = [];
  documentsBase64: { url: string; base64: string; fileExtension: string }[] =
    [];
  documentsDownload = signal<any>({});
  mode = signal<'share' | 'download'>('share');
  submitting = signal<boolean>(false);
  loanDetails = $localize`loan-details`;
  registeredPayments = $localize`registered-payments`;
  supportingDocument = $localize`supporting-document`;
  public isBeneficiary = signal<boolean>(
    this.sessionService.params()['profileId'] === PROFILE_IDS.BasicBeneficiary
  );

  constructor() {
    const documentLoaded = this.isBeneficiary()
      ? this.data.credit?.operation.additionalData.formalDocumentId
      : this.data.credit?.documentLoaded;
    this.form = this.formBuilder.group({
      creditDetail: false,
      payments: false,
      supportDoc: false
    });
    if (this.data.download) {
      this.mode.set('download');
    }
    if (documentLoaded === '') {
      this.form.get('supportDoc')?.disable();
    }
    this.credit.set(this.data.credit);
  }

  /**
   * @description Limpia los documentos seleccionados y el estado del formulario
   */
  clearDocuments(): void {
    this.form.reset();
    this.documents = [];
    this.dowloadedDocuments.set(false);
  }

  /**
   * @description Descarga los documentos seleccionados (detalle del crédito, pagos, documento soporte)
   * @returns {Promise<void>}
   */
  async download(): Promise<void> {
    if (
      this.data.credit &&
      (this.form.get('creditDetail')?.value ||
        this.form.get('payments')?.value ||
        this.form.get('supportDoc')?.value)
    ) {
      const lenderId = this.sessionService.settings()?.lenderId;
      const operationNumber = String(this.isBeneficiary()
        ? this.data.credit.operation.number
        : this.data.credit.operationNumber);
      const documentLoaded = this.isBeneficiary()
        ? this.data.credit?.operation.additionalData.formalDocumentId
        : this.data.credit?.documentLoaded;
      try {
        this.submitting.set(true);
        this.form.disable();
        const response: any = await this.apiService.call({
          microServiceName: 'WSMPCreditManagement',
          resourcePath: `/document/process-share-information`,
          method: 'POST',
          useToken: true,
          body: {
            lenderId,
            operationNumber,
            documentLoaded,
            documents: {
              creditDetail: this.form.get('creditDetail')?.value ?? false,
              payments: this.form.get('payments')?.value ?? false,
              supportDoc: this.form.get('supportDoc')?.value ?? false
            }
          },
          bodyEncrypted: true,
          responseEncrypted: true
        });
        this.documents = [];
        this.documentsBase64 = [];
        Object.keys(response.data.documents).forEach((key, index) => {
          const document = response.data.documents[key];

          if (document.base64) {
            // Decodificar Base64 y crear un Blob
            const binary = atob(document.base64); // Decodifica Base64
            const binaryLength = binary.length;
            const bytes = new Uint8Array(binaryLength);
            const mimeType: string =
              this.getMimeType(document.fileExtension) || 'application/pdf';
            for (let i = 0; i < binaryLength; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mimeType }); // Cambia el tipo MIME según el contenido del documento.

            let documentName = '';
            switch (key) {
              case 'creditDetail':
                documentName = this.loanDetails;
                break;
              case 'payments':
                documentName = this.registeredPayments;
                break;
              case 'supportDoc':
                documentName = this.supportingDocument;
                break;
            }

            const file = new File(
              [blob],
              `${documentName}-${operationNumber}.${document.fileExtension}`,
              {
                type: mimeType
              }
            );
            const datauri = `data:${mimeType};base64,${document.base64}`;
            this.documentsDownload.update(item => {
              item[key] = datauri;
              return item;
            });
            this.documentsBase64.push({
              url: datauri,
              base64: document.base64,
              fileExtension: document.fileExtension
            });
            this.documents.push(file);
          }
        });
        this.dowloadedDocuments.set(true);
      } catch (err) {
        throw err;
      } finally {
        this.submitting.set(false);
        this.form.enable();
        if (documentLoaded === '') {
          this.form.get('supportDoc')?.disable();
        }
      }
    } else {
      this.messageService.show(
        $localize`Please select at least one document to download.`,
        { type: 'modal' }
      );
    }
  }

  /**
   * @description Comparte los documentos descargados usando la Web Share API
   * @returns {Promise<void>}
   */
  async share(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        this.nativeShare();
        return;
      }
      this.sharing.set(true);
      await navigator.share({
        files: this.documents,
        title: '',
        text: ''
      });
    } catch (err) {
      this.messageService.show($localize`The document was not shared.`, {
        class: 'ft-message--danger',
        icon: 'error'
      });
    } finally {
      this.sharing.set(false);
    }
  }

  /**
   * @description Comparte los documentos usando la API nativa de Capacitor
   * @private
   * @returns {Promise<void>}
   */
  async nativeShare(): Promise<void> {
    let files: string[] = [];
    const zip = new JSZip();
    for (let i = 0; i < this.documentsBase64.length; i++) {
      let data = this.documentsBase64[i].base64;
      let path = this.documents[i].name;
      if (this.documentsBase64.length > 1) {
        zip.file(path, data, { base64: true });
        if (this.documentsBase64.length - 1 === i) {
          this.progressService.show({
            modal: true
          });
          data = await zip.generateAsync({ type: 'base64' });
          path = 'files.zip';
        } else {
          continue;
        }
      }
      await Filesystem.writeFile({
        path,
        data,
        directory: Directory.Cache
      });
      const uriResult = await Filesystem.getUri({
        directory: Directory.Cache,
        path
      });
      files.push(uriResult.uri);
    }
    this.progressService.hide();
    Share.share({
      files,
      title: ''
    });
  }

  /**
   * @description Obtiene el MIME type según la extensión del archivo
   * @param {string} fileExtension - Extensión del archivo (ej: pdf, docx)
   * @returns {string | undefined} MIME type o undefined si no se encuentra
   */
  getMimeType(fileExtension: string): string | undefined {
    return environment.filesAllowed.find(f => f.extension === fileExtension)
      ?.mimetype;
  }

  /**
   * @description Método de submit del formulario (placeholder, sin lógica)
   */
  submit(): void {}
}
